import { getFolderMap, getKeywordsMap, getSetting, setSelectedEmailsInfo } from "./taskpane/taskpane";
import { EmailInfo } from "./types/emailInfo";
import { sendInsightMessage } from "./notificationHandler";
import { EmailData } from "../../common/src/types/types";

// Tracks the folder/list element currently highlighted during a drag so the
// dragover handler can skip redundant DOM work when the target hasn't changed.
let lastDragOverTarget: HTMLElement | null = null;

export async function handleEmailSelectionChange(event: any = null): Promise<void> {
    let selectedItems: Office.SelectedItemDetails[] = [];
    const emailInfos: EmailInfo[] = [];

    if (!Office.context.mailbox) {
        console.warn("No mailbox found in context.");
        setSelectedEmailsInfo([]);
        window.dispatchEvent(new CustomEvent('email-selection-changed', {
            detail: { emails: [] }
        }));
        return;
    }

    const item = Office.context.mailbox.item;

    try {
        await new Promise<void>((resolve) => {
            Office.context.mailbox.getSelectedItemsAsync((asyncResult) => {
                if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                    console.error("Failed to get selected items:", asyncResult.error);
                    resolve();
                }
                selectedItems = asyncResult.value;
                resolve();
            });
        });

        if (selectedItems && selectedItems.length > 0) {
            const emailToSet = selectedItems.map((item => ({
                id: item.itemId,
                subject: item.subject,
                hasAttachments: item.hasAttachment,
                internetMessageId: item.internetMessageId,
            })))
            setSelectedEmailsInfo(emailToSet);
            window.dispatchEvent(new CustomEvent('email-selection-changed', {
                detail: { emails: emailToSet }
            }));
        }
    } catch (error) {
        console.error("Error handling email selection change:", error);
        setSelectedEmailsInfo([]);
        window.dispatchEvent(new CustomEvent('email-selection-changed', {
            detail: { emails: [] }
        }));
    }
}

export async function itemChangeHandler(): Promise<void> {
    const smartSuggestionsEnabled = getSetting('smartSuggestionsEnabled', true);
    const item = Office.context.mailbox.item;
    if (smartSuggestionsEnabled && item && item.itemType === "message") {
        const keywords = getKeywordsMap();
        const subject = item.subject;
        let body = "";
        await new Promise<void>((resolve) => {
            item.body.getAsync("html", (bodyResult) => {
                if (bodyResult.status === Office.AsyncResultStatus.Succeeded) {
                    body = bodyResult.value;
                } else {
                    console.error("Failed to get email body on item change:", bodyResult.error);
                }
                resolve();
            });
        });

        const suggestedFolders: string[] = [];
        let foundKeyword: string | null = null;
        keywords.forEach((keywords, folder) => {
            for (const keyword of keywords) {
                const keywordLower = keyword.toLowerCase();
                if (subject.toLowerCase().includes(keywordLower) || body.toLowerCase().includes(keywordLower)) {
                    foundKeyword = keyword;
                    suggestedFolders.push(folder);
                    break;
                }
            }
        });

        if (suggestedFolders.length === 0) {
            return;
        } else {
            const folderName = getFolderMap().get(suggestedFolders[0]) || suggestedFolders[0];
            let suggestionMessage = "";
            if (suggestedFolders.length === 1) {
                suggestionMessage = `Email contains the keyword '${foundKeyword}', you might want to file it in: ${folderName}.`;
            } else if (suggestedFolders.length > 1) {
                suggestionMessage = `Email contains the keyword '${foundKeyword}', you might want to file it in one of these folders: ${folderName} +${suggestedFolders.length - 1} more ...`;
            }
            else {
                suggestionMessage = "No smart folder suggestions available for this email.";
            }

            sendInsightMessage(
                "smartFolderSuggestions",
                suggestionMessage,
                "File this email",
                { suggestedFolders: suggestedFolders }
            );
        }

    }
}

async function getAdditionalEmailInfo(emailId: string): Promise<EmailInfo | undefined> {
    if (!Office.context.requirements.isSetSupported("Mailbox", "1.3")) {
        console.warn("Unsupported API set for getting additional email info.");
        return undefined;
    }
    let item = undefined;
    await new Promise<void>((resolve) => {
        Office.context.mailbox.loadItemByIdAsync(emailId, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                const message = result.value as Office.LoadedMessageRead;
                const emailInfo = {
                    id: message.itemId,
                    from: message.sender,
                    to: message.to,
                    cc: message.cc,
                    subject: message.subject,
                    body: "",
                    attachments: message.attachments,
                    date: message.dateTimeCreated
                }
                message.body.getAsync("html", (bodyResult) => {
                    if (bodyResult.status === Office.AsyncResultStatus.Succeeded) {
                        emailInfo.body = bodyResult.value;
                    } else {
                        console.error("Failed to get email body:", bodyResult.error);
                    }
                    message.unloadAsync((asyncResult) => {
                        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        } else {
                            console.error("Failed to unload message:", asyncResult.error)
                        };
                        item = emailInfo;
                        resolve();
                    }
                    );
                });
            } else {
                console.error("Failed to load item by ID:", result.error);
                return;
            }
        });
    });

    return item;
}

export function handleDragAndDrop(event: Office.DragAndDropEventArgs): void {
    const eventData = event.dragAndDropEventData;

    function dispatchEvent(element: Element, type: "dragover" | "drop") {
        const event = new DragEvent(type, {
            bubbles: true,
            cancelable: true
        });
        element.dispatchEvent(event);
    }

    function locateTargetElement(x: number, y: number): HTMLElement | null {
        const el = document.elementFromPoint(x, y);

        const targetElement = el?.closest(".folder-item, .email-list-container") as HTMLElement | null;
        if (targetElement) {
        } else {
            console.warn("No target element found at the given coordinates.");
        }
        return targetElement;
    }

    function clearDragOverHighlight() {
        document.querySelectorAll(".drag-over").forEach(el =>
            el.classList.remove("drag-over")
        );
    }

    if (eventData.type === "dragover") {
        //@ts-ignore - Office typings are incorrect for this event data
        const targetElement = locateTargetElement(eventData.taskPaneX, eventData.taskPaneY);

        // The dragover event fires continuously while the pointer moves over the
        // task pane. Skip the DOM/highlight work (and the React re-render it
        // triggers) unless the hovered target actually changed, to keep the UI
        // responsive while dragging large selections.
        if (targetElement === lastDragOverTarget) {
            return;
        }
        lastDragOverTarget = targetElement;

        clearDragOverHighlight();
        if (targetElement) {
            targetElement.classList.add("drag-over");
            dispatchEvent(targetElement, "dragover");
        } else {
            console.warn("Dragover outside of a target item.");
        }

    }

    // Get the file name and the contents of the items dropped into the task pane.
    if (eventData.type == "drop") {
        lastDragOverTarget = null;
        //@ts-ignore - Office typings are incorrect for this event data
        const targetElement = locateTargetElement(eventData.taskPaneX, eventData.taskPaneY);
        clearDragOverHighlight();
        if (targetElement) {
            dispatchEvent(targetElement, "drop");
        } else {
            console.warn("Dropped outside of a target item.");
        }
    }
}

export function setEfileCategories(): void {
    const requiredCategories = [
        { displayName: "E-File: Queued", color: Office.MailboxEnums.CategoryColor.Preset12 },
        { displayName: "E-File: Archived", color: Office.MailboxEnums.CategoryColor.Preset6 },
        { displayName: "E-File: Error Archiving", color: Office.MailboxEnums.CategoryColor.Preset2 },
    ]

    Office.context.mailbox.masterCategories.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
            const categories = result.value;
            const efileCategories = categories.filter(category => category.displayName.toLowerCase().startsWith("e-file"));

            const categoriesToAdd: { displayName: string, color: Office.MailboxEnums.CategoryColor }[] = requiredCategories.filter(requiredCategory => !efileCategories.some(existingCategory => existingCategory.displayName.toLowerCase() === requiredCategory.displayName.toLowerCase()));

            if (categoriesToAdd.length > 0) {
                console.log("Adding missing categories to master list...");
                Office.context.mailbox.masterCategories.addAsync(categoriesToAdd, function (asyncResult) {
                    console.log("addAsync callback executed with status:", asyncResult.status);
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                    } else {
                        console.log("masterCategories.addAsync call failed with error: " + asyncResult.error.message);
                    }
                });

            } else {
            }
        } else {
            console.error("Failed to get master categories:", result.error);
        }
    });
}